import { useEffect, useState } from "react";
import { Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useProfile } from "@/context/ProfileContext";
import { errorMessage } from "@/lib/api";
import { GenreSelect, Field } from "./FormBits";
import { FilePick } from "./UploadForm";

export const TrackEditDialog = ({ track, onClose, onSaved }) => {
  const { request } = useProfile();
  const [form, setForm] = useState(null);
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (track) { setForm({ title: track.title, artist_name: track.artist_name, genre: track.genre, description: track.description }); setCover(null); }
  }, [track]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let updated = await request("patch", `/tracks/${track.id}`, form);
      if (cover) { const fd = new FormData(); fd.append("cover", cover); updated = await request("post", `/tracks/${track.id}/cover`, fd); }
      toast.success("Track updated");
      onSaved(updated);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!track} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="track-edit-dialog" className="bg-nw-bg2 border-white/10 text-slate-100 max-w-[92vw] sm:max-w-md rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl chrome-text">Edit track</DialogTitle>
          <DialogDescription className="text-slate-400">Update your track details.</DialogDescription>
        </DialogHeader>
        {form && (
          <form onSubmit={save} className="space-y-4">
            <Field label="Song title"><input data-testid="track-edit-title-input" required maxLength={100} value={form.title} onChange={set("title")} className="nw-input" /></Field>
            <Field label="Artist name"><input data-testid="track-edit-artist-input" required maxLength={80} value={form.artist_name} onChange={set("artist_name")} className="nw-input" /></Field>
            <Field label="Genre"><GenreSelect testId="track-edit-genre-select" value={form.genre} onChange={set("genre")} /></Field>
            <Field label="Description"><textarea data-testid="track-edit-description-input" rows={3} maxLength={1000} value={form.description} onChange={set("description")} className="nw-input resize-none" /></Field>
            <FilePick testId="track-edit-cover-input" accept="image/jpeg,image/png,image/webp" icon={ImagePlus} label="Replace cover (optional)" file={cover} onPick={setCover} />
            <div className="flex gap-3 pt-2">
              <button type="button" data-testid="track-edit-cancel-button" onClick={onClose} className="btn-chrome flex-1 py-3 text-sm">Cancel</button>
              <button data-testid="track-edit-save-button" disabled={busy} className="btn-red flex-1 py-3 text-sm disabled:opacity-60">{busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Save"}</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
